// Re-export rational arithmetic from FloodUtils
export type { Rational } from '../flood/FloodUtils.js';
export {
    gcd,
    reduceRational,
    addRationals,
    subtractRationals,
    multiplyRationals,
    divideRational,
    compareRationals,
    rationalToNumber,
    colToLetter,
    rationalToString
} from '../flood/FloodUtils.js';

import type { Rational } from '../flood/FloodUtils.js';

// Diagonal wall types
export type DiagonalWallType = 'f' | 'b' | 'x' | null;
// f = forward diagonal (/)
// b = backward diagonal (\)
// x = cross (both diagonals)
// null = no wall

// Quadrants within a cell
export type Quadrant = 'u' | 'd' | 'l' | 'r';

// Sections - groups of connected quadrants within a cell
// Based on wall type:
// - null: single section 'uldr' (all connected)
// - 'f' (/): two sections 'ul' and 'dr' (wall from bottom-left to top-right)
// - 'b' (\): two sections 'dl' and 'ur' (wall from top-left to bottom-right)
// - 'x': four sections 'u', 'l', 'd', 'r'
export type SectionId = 'uldr' | 'ur' | 'dl' | 'ul' | 'dr' | 'u' | 'l' | 'd' | 'r';

// Section tags for flow resolution (similar to CellTag in Flood)
// `parent` is the upstream *crash* — where the falling water landed — carried
// unchanged along a whole spread so the basin it feeds can be found again. `via` on
// a spread is the neighbouring section that tagged this one: the step the water
// actually took to get here. Only the spill hints need it, and only they should use
// it — a crash can be several cells away and on the far side of a wall. (Same split
// as Flood's `CellTag`.)
export type SectionTag =
    | { type: 'drop'; parent: string | null; rate: Rational | null; resolved: boolean }
    | { type: 'crash'; parent: string | null; rate: Rational; resolved: boolean }
    | { type: 'spread'; parent: string | null; via?: string; resolved: boolean };

// Basin info collected from flow tree
export interface Basin {
    sections: string[];  // section keys
    rate: Rational;
}

// A spill point: a cell where water tips over an edge and starts falling.
// `from` is the horizontal side the water arrived from (null = straight down).
export interface DropHint {
    row: number;
    col: number;
    from: 'W' | 'E' | null;
}

// Snapshot of simulation state at a point in time
export interface DelugeSnapshot {
    filled: Set<string>;
    waterLevel: Map<string, Rational>;
    flowState: Map<string, SectionTag>;
    basins: Basin[];
}

// Cell key (same as Flood)
export function cellKey(row: number, col: number): string {
    return `${row},${col}`;
}

// Section key format: "row,col,sectionId"
export function sectionKey(row: number, col: number, sectionId: SectionId): string {
    return `${row},${col},${sectionId}`;
}

// Parse section key back to components
export function parseSectionKey(key: string): { row: number; col: number; sectionId: SectionId } {
    const parts = key.split(',');
    return {
        row: Number(parts[0]),
        col: Number(parts[1]),
        sectionId: parts[2] as SectionId
    };
}

// Get sections for a wall type
export function getSections(wallType: DiagonalWallType): SectionId[] {
    switch (wallType) {
        case null: return ['uldr'];
        case 'f': return ['ul', 'dr'];  // / wall: upper-left and down-right
        case 'b': return ['dl', 'ur'];  // \ wall: down-left and upper-right
        case 'x': return ['u', 'l', 'd', 'r'];
    }
}

// Get quadrants contained in a section
export function getQuadrants(sectionId: SectionId): Quadrant[] {
    switch (sectionId) {
        case 'uldr': return ['u', 'l', 'd', 'r'];
        case 'ur': return ['u', 'r'];
        case 'dl': return ['d', 'l'];
        case 'ul': return ['u', 'l'];
        case 'dr': return ['d', 'r'];
        case 'u': return ['u'];
        case 'l': return ['l'];
        case 'd': return ['d'];
        case 'r': return ['r'];
    }
}

// Get the number of quadrants in a section (for fill rate calculation)
export function getSectionSize(sectionId: SectionId): number {
    return getQuadrants(sectionId).length;
}

// Find which section contains a given quadrant for a wall type
export function getSectionContaining(quadrant: Quadrant, wallType: DiagonalWallType): SectionId {
    const sections = getSections(wallType);
    for (const section of sections) {
        if (getQuadrants(section).includes(quadrant)) {
            return section;
        }
    }
    // Should never happen
    return 'uldr';
}

// Check if a section contains a quadrant
export function sectionContains(sectionId: SectionId, quadrant: Quadrant): boolean {
    return getQuadrants(sectionId).includes(quadrant);
}

// Check if a section contains both 'u' and 'd' (drop can pass through)
export function sectionConnectsVertically(sectionId: SectionId): boolean {
    const quadrants = getQuadrants(sectionId);
    return quadrants.includes('u') && quadrants.includes('d');
}

// Get the neighbor section in an adjacent cell
// Returns the section key for the neighbor, or null if at boundary
export function getNeighborSection(
    row: number,
    col: number,
    direction: Quadrant,
    getWallType: (r: number, c: number) => DiagonalWallType,
    gridSize: number
): string | null {
    let neighborRow = row;
    let neighborCol = col;
    let entryQuadrant: Quadrant;

    switch (direction) {
        case 'u':
            if (row === 0) return null;
            neighborRow = row - 1;
            entryQuadrant = 'd';
            break;
        case 'd':
            if (row === gridSize - 1) return null;
            neighborRow = row + 1;
            entryQuadrant = 'u';
            break;
        case 'l':
            if (col === 0) return null;
            neighborCol = col - 1;
            entryQuadrant = 'r';
            break;
        case 'r':
            if (col === gridSize - 1) return null;
            neighborCol = col + 1;
            entryQuadrant = 'l';
            break;
    }

    const neighborWallType = getWallType(neighborRow, neighborCol);
    const neighborSection = getSectionContaining(entryQuadrant, neighborWallType);
    return sectionKey(neighborRow, neighborCol, neighborSection);
}

// Get all external neighbor sections for a given section
export function getExternalNeighbors(
    row: number,
    col: number,
    sectionId: SectionId,
    getWallType: (r: number, c: number) => DiagonalWallType,
    gridSize: number
): string[] {
    const neighbors: string[] = [];
    const quadrants = getQuadrants(sectionId);

    for (const q of quadrants) {
        const neighbor = getNeighborSection(row, col, q, getWallType, gridSize);
        if (neighbor !== null && !neighbors.includes(neighbor)) {
            neighbors.push(neighbor);
        }
    }

    return neighbors;
}

// Get only horizontal neighbors (for spread behavior)
export function getHorizontalNeighbors(
    row: number,
    col: number,
    sectionId: SectionId,
    getWallType: (r: number, c: number) => DiagonalWallType,
    gridSize: number
): string[] {
    const neighbors: string[] = [];
    const quadrants = getQuadrants(sectionId);

    if (quadrants.includes('l')) {
        const neighbor = getNeighborSection(row, col, 'l', getWallType, gridSize);
        if (neighbor !== null) neighbors.push(neighbor);
    }
    if (quadrants.includes('r')) {
        const neighbor = getNeighborSection(row, col, 'r', getWallType, gridSize);
        if (neighbor !== null) neighbors.push(neighbor);
    }

    return neighbors;
}

// Cycle to next wall type: null -> 'f' -> 'b' -> 'x' -> null
export function cycleWallType(current: DiagonalWallType): DiagonalWallType {
    switch (current) {
        case null: return 'f';
        case 'f': return 'b';
        case 'b': return 'x';
        case 'x': return null;
    }
}

// Wall connectivity helpers for validation

// Get the corners that a wall type touches
// Returns array of [row_offset, col_offset] pairs
// Forward / touches: bottom-left (1, 0) and top-right (0, 1)
// Backward \ touches: top-left (0, 0) and bottom-right (1, 1)
export function getWallCorners(wallType: DiagonalWallType): Array<[number, number]> {
    if (wallType === null) return [];
    const corners: Array<[number, number]> = [];

    if (wallType === 'f' || wallType === 'x') {
        // Forward diagonal touches bottom-left and top-right corners
        corners.push([1, 0]); // bottom-left
        corners.push([0, 1]); // top-right
    }
    if (wallType === 'b' || wallType === 'x') {
        // Backward diagonal touches top-left and bottom-right corners
        corners.push([0, 0]); // top-left
        corners.push([1, 1]); // bottom-right
    }

    return corners;
}

// Check if a wall touches the boundary
export function wallTouchesBoundary(row: number, col: number, wallType: DiagonalWallType, gridSize: number): boolean {
    if (wallType === null) return false;

    // Forward / touches boundary if:
    // - bottom-left corner at left edge (col === 0) or bottom edge (row === gridSize - 1)
    // - top-right corner at right edge (col === gridSize - 1) or top edge (row === 0)
    if (wallType === 'f' || wallType === 'x') {
        if (col === 0 || row === gridSize - 1) return true; // bottom-left at edge
        if (col === gridSize - 1 || row === 0) return true; // top-right at edge
    }

    // Backward \ touches boundary if:
    // - top-left corner at left edge (col === 0) or top edge (row === 0)
    // - bottom-right corner at right edge (col === gridSize - 1) or bottom edge (row === gridSize - 1)
    if (wallType === 'b' || wallType === 'x') {
        if (col === 0 || row === 0) return true; // top-left at edge
        if (col === gridSize - 1 || row === gridSize - 1) return true; // bottom-right at edge
    }

    return false;
}

// Get adjacent wall cells that share a corner with this cell's wall
export function getAdjacentWallCells(
    row: number,
    col: number,
    wallType: DiagonalWallType,
    gridSize: number
): Array<{ row: number; col: number }> {
    if (wallType === null) return [];

    const adjacent: Array<{ row: number; col: number }> = [];
    const corners = getWallCorners(wallType);

    // For each corner this wall touches, find neighbor cells that could share that corner
    for (const [rowOffset, colOffset] of corners) {
        // The corner is at (row + rowOffset, col + colOffset) in corner-space
        // Cells that share this corner are:
        // - (row + rowOffset - 1, col + colOffset - 1) - if it touches bottom-right
        // - (row + rowOffset - 1, col + colOffset) - if it touches bottom-left
        // - (row + rowOffset, col + colOffset - 1) - if it touches top-right
        // Current cell is (row, col)

        const cornerRow = row + rowOffset;
        const cornerCol = col + colOffset;

        // Check cells that could share this corner (4 cells can share a corner)
        const candidates = [
            { r: cornerRow - 1, c: cornerCol - 1 }, // needs bottom-right
            { r: cornerRow - 1, c: cornerCol },     // needs bottom-left
            { r: cornerRow, c: cornerCol - 1 },     // needs top-right
            { r: cornerRow, c: cornerCol },         // needs top-left
        ];

        for (const { r, c } of candidates) {
            // Skip if out of bounds or same cell
            if (r < 0 || r >= gridSize || c < 0 || c >= gridSize) continue;
            if (r === row && c === col) continue;

            // Add to adjacent list (will check wall type later during validation)
            if (!adjacent.some(a => a.row === r && a.col === c)) {
                adjacent.push({ row: r, col: c });
            }
        }
    }

    return adjacent;
}

// Check if two walls in adjacent cells actually connect (share a corner)
export function wallsConnect(
    row1: number, col1: number, wallType1: DiagonalWallType,
    row2: number, col2: number, wallType2: DiagonalWallType
): boolean {
    if (wallType1 === null || wallType2 === null) return false;

    const corners1 = getWallCorners(wallType1);
    const corners2 = getWallCorners(wallType2);

    // Check if any corner from wall1 matches any corner from wall2
    for (const [r1, c1] of corners1) {
        const corner1Row = row1 + r1;
        const corner1Col = col1 + c1;

        for (const [r2, c2] of corners2) {
            const corner2Row = row2 + r2;
            const corner2Col = col2 + c2;

            if (corner1Row === corner2Row && corner1Col === corner2Col) {
                return true;
            }
        }
    }

    return false;
}

// Vortex-specific types for multi-slice flow algorithm
export interface DelugeSliceState {
    ancestry: string[];       // Sequence of entry-gate cellKeys traversed to reach this slice
    flowState: Map<string, SectionTag>;
}

export interface VortexDelugeSnapshot {
    filled: Set<string>;
    waterLevel: Map<string, Rational>;
    slices: Map<string, DelugeSliceState>;  // sliceId -> DelugeSliceState
    basins: Basin[];
}

// Format section key to readable name
export function sectionKeyToName(sk: string): string {
    const { row, col, sectionId } = parseSectionKey(sk);
    const letter = String.fromCharCode(65 + col);
    return `${letter}${row}:${sectionId}`;
}
